(ns arthur.ui.layout
  "How big the panes are, which of them are shut, and at what zoom the two
  zoomable surfaces are drawn.

  ONE NAMESPACE FOR STATE, EVENTS AND WIDGETS, which is not this repo's shape
  anywhere else. The reason is that all three are the same handful of numbers:
  `:pool` is a pane's width in pixels, a grid track, a drag's clamp and a
  button's `aria` label, and splitting six integers across `db`, `events/` and
  `ui/` would be more wiring than state. Nothing below is about the document.

  The panes stay sized in PIXELS rather than fractions. A pane whose width is a
  share of the window changes size when the window does, and three of these hold
  lists of fixed-width rows — a label column, a swatch strip, an inspector — so
  what somebody drags the edge to is the width they wanted, at any window size.

  THE TIMELINE DOES NOT SHUT AWAY ITS HEAD. Shutting it leaves the transport
  strip on screen, because that strip is play, pause and the frame readout: a
  window with nowhere to press play is not a smaller window, it is a broken one.
  The library and the inspector unmount instead — on a narrow screen they are
  drawers over the stage rather than columns beside it, which is the stylesheet's
  half of this (see `@media` in app.css), and a drawer that is shut should not be
  holding video thumbnails live."
  (:require [arthur.domain.onion :as onion]
            [arthur.ui.menu :as menu]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(def ^:private narrow?
  "A phone-shaped window, read ONCE at load. It decides what the layout opens
  at and nothing after that: a resize past the breakpoint re-lays the grid out
  through the stylesheet, and silently throwing away the sizes somebody dragged
  because they turned their tablet sideways would be a worse answer than a
  layout that is briefly the wrong shape. Guarded for the node test build, which
  has no window."
  (and (exists? js/window) (< (.-innerWidth js/window) 760)))

(def normal
  "The layout nobody chose: what the window opens at, and what the zoom readouts
  go back to when pressed. Narrow windows open with both side panes shut and the
  stage at 1:1, because 320x200 at 2x is wider than the screen it would be the
  only thing on."
  (merge {:pool 210 :params 250 :time 232 :stage 2 :tl 1 :shut #{}}
         (when narrow? {:time 150 :stage 1 :shut #{:pool :params}})))

(def ^:private limits
  "[lo hi] per key — pixels for a pane, a multiplier for a zoom. The low end of
  `:time` is the transport strip plus a row, so dragging the timeline shut and
  shutting it are the same shape of window."
  {:pool [120 480] :params [150 560] :time [44 660] :stage [0.1 16] :tl [1 24]})

(defn- clamp [k v]
  (let [[lo hi] (limits k)] (max lo (min hi v))))

(rf/reg-sub ::state (fn [db _] (merge normal (get-in db [:ui :layout]))))
(rf/reg-sub ::zoom :<- [::state] (fn [state [_ k]] (k state)))

(rf/reg-event-db
 ::set
 ;; Pixels for a pane, a multiplier for a zoom, clamped either way.
 (fn [db [_ k v]] (assoc-in db [:ui :layout k] (clamp k v))))

(rf/reg-event-db
 ::toggle
 (fn [db [_ k]]
   (update-in db [:ui :layout :shut]
              #(let [shut (or % (:shut normal))]
                 (if (contains? shut k) (disj shut k) (conj shut k))))))

(defn vars
  "The grid, as custom properties for `.app` to resolve its tracks against. A
  shut pane is a zero-width track AND a zero-width grip: there is no edge to
  drag when there is nothing on the other side of it."
  [{:keys [pool params time tl shut]}]
  (let [gone? #(contains? shut %)]
    {"--pool-w"   (if (gone? :pool) "0px" (str pool "px"))
     "--params-w" (if (gone? :params) "0px" (str params "px"))
     ;; Head-height, not zero: see the namespace docstring.
     "--time-h"   (if (gone? :time) "22px" (str time "px"))
     "--grip-pool"   (if (gone? :pool) "0px" "4px")
     "--grip-params" (if (gone? :params) "0px" "4px")
     "--grip-time"   (if (gone? :time) "0px" "4px")
     ;; The timeline's zoom is entirely the stylesheet's: every mark in the
     ;; tracks is positioned as a percentage of their width, so widening them
     ;; widens the frame grid, the spans, the ruler and the playhead together.
     "--tl-zoom"  tl}))

;; Which grip is being dragged, and from where. A PLAIN atom: it lives for the
;; length of one drag and nothing renders from it, as in `ui/stage`.
(defonce ^:private sizing (atom nil))

(defn grip
  "The edge between two panes, as something you can grab. `sign` is which way
  the pointer grows the pane — the inspector's edge is on its left, so dragging
  left makes it wider."
  [k size axis sign]
  (let [at (fn [^js e] (if (= :col axis) (.-clientX e) (.-clientY e)))]
    [:div
     {:class (str "grip grip-" (name k) " " (name axis))
      :role "separator"
      :aria-label (str "resize the " (name k) " pane")
      :on-pointer-down
      (fn [^js e]
        (.preventDefault e)
        ;; The size is read HERE and the drag is absolute from it, rather than
        ;; accumulating per-move deltas: a clamped increment loses the pointer
        ;; at the limits, and the pane then lags the finger coming back.
        (reset! sizing [(at e) size])
        ;; Capture is what keeps a drag alive once the pointer leaves a 4px
        ;; target. As in `ui/timeline`'s ruler it throws on a synthesised
        ;; pointer id, and what the catch loses is the drag and nothing else.
        (try (.setPointerCapture (.-currentTarget e) (.-pointerId e))
             (catch :default _ nil)))
      :on-pointer-move
      (fn [^js e]
        (when-let [[from from-size] @sizing]
          (rf/dispatch [::set k (+ from-size (* sign (- (at e) from)))])))
      :on-pointer-up     (fn [_] (reset! sizing nil))
      :on-pointer-cancel (fn [_] (reset! sizing nil))}]))

(defn panes
  "Which panes are open, as a segmented control in the top bar. In the top bar
  because a shut pane has no head left to carry its own handle, and because on a
  narrow screen this is the whole of the navigation."
  []
  (let [{:keys [shut]} @(rf/subscribe [::state])]
    [:div.seg.panes {:aria-label "panes"}
     (doall
      (for [[k label] [[:pool "pool"] [:params "inspector"] [:time "timeline"]]]
        (let [open? (not (contains? shut k))]
          ^{:key k}
          [:button {:class (when open? "on")
                    :aria-pressed open?
                    :title (str (if open? "hide the " "show the ") label)
                    :on-click #(rf/dispatch [::toggle k])}
           label])))]))

;; The work area, `{:stage [w h] :rect [x0 y0 x1 y1]}` in stage pixels: the stage,
;; everything drawn so far and a stage round both, and every view zoomed to. It
;; grows as things are drawn further out, so there is always room to draw
;; further, and it never shrinks, so there is always room to zoom about the
;; pointer. A Reagent atom: the stage lays itself out by it.
(defonce board (r/atom nil))

(defn hold!
  "Make the work area hold `[x0 y0 x1 y1]`, on stage `[w h]`, in steps of 64 so
  a shape moving out does not lay the stage out again every frame."
  [[x0 y0 x1 y1] stage]
  (let [step #(* 64 (% (/ %2 64)))
        want [(step js/Math.floor x0) (step js/Math.floor y0)
              (step js/Math.ceil x1) (step js/Math.ceil y1)]
        {was :stage rect :rect} @board
        rect (if (= was stage) (mapv #(%1 %2 %3) [min min max max] rect want) want)]
    (when (not= rect (:rect @board))
      (reset! board {:stage stage :rect rect}))))

;; The stage point `zoom-at!` holds under the pointer, `{:x :y :client-x :client-y
;; :due? :scroll}`. Kept between zooms, so a pinch is anchored on one point and
;; not on each event's re-measurement of it, which is off by the rounding of the
;; last scroll and adds up over a gesture to a slide. Measured again when the
;; pointer moves or the view is scrolled by anything else.
(defonce ^:private anchor (atom nil))

(defn- laid-zoom
  "The zoom the stage box is laid out at, which is behind app-db's until React
  commits — later than `r/flush`, under a React 18 root."
  [^js wrap]
  (/ (.-width (.getBoundingClientRect wrap)) (js/parseFloat (.getAttribute wrap "data-width"))))

(declare zoomed!)

(defn zoom-at!
  "Zoom to `scale`, keeping the stage point under the pointer under it. The
  scroll that does so waits for the stage box to be laid out at the new zoom —
  see `zoomed!`. The work area grows to the view that will be on screen, in the
  same layout, or that scroll could not reach there and the stage would be
  pushed into the corner."
  [^js el client-x client-y scale]
  (let [wrap (.querySelector el ".stage-wrap")
        w (js/parseFloat (.getAttribute wrap "data-width"))
        h (js/parseFloat (.getAttribute wrap "data-height"))
        z (clamp :stage scale)
        was @anchor
        {:keys [x y]} (if (and was (= [client-x client-y] [(:client-x was) (:client-y was)])
                               (or (:due? was) (= (:scroll was) [(.-scrollLeft el) (.-scrollTop el)])))
                        was
                        (let [box (.getBoundingClientRect wrap) laid (laid-zoom wrap)]
                          {:x (/ (- client-x (.-left box)) laid) :y (/ (- client-y (.-top box)) laid)}))
        a (.getBoundingClientRect el)
        cx (- client-x (.-left a)) cy (- client-y (.-top a))]
    (reset! anchor {:x x :y y :client-x client-x :client-y client-y :due? true})
    (hold! [(- x (/ cx z)) (- y (/ cy z))
            (+ x (/ (- (.-clientWidth el) cx) z)) (+ y (/ (- (.-clientHeight el) cy) z))]
           [w h])
    (rf/dispatch-sync [::set :stage z])
    ;; Already laid out at this size, as at the end of the range: the box will
    ;; not be laid out again, so do not wait for it.
    (when (< (js/Math.abs (- (* z w) (.-width (.getBoundingClientRect wrap)))) 0.5)
      (zoomed! el))))

(defn zoomed!
  "The stage box has been laid out again: if at a zoom from `zoom-at!`, scroll
  its point back under the pointer. Before paint, so it never shows elsewhere."
  [^js el]
  (when-let [{:keys [x y client-x client-y due?]} @anchor]
    (when due?
      (let [wrap (.querySelector el ".stage-wrap")
            box (.getBoundingClientRect wrap)
            z (laid-zoom wrap)]
        (set! (.-scrollLeft el) (+ (.-scrollLeft el) (- (+ (.-left box) (* x z)) client-x)))
        (set! (.-scrollTop el) (+ (.-scrollTop el) (- (+ (.-top box) (* y z)) client-y)))
        (swap! anchor assoc :due? false :scroll [(.-scrollLeft el) (.-scrollTop el)])))))

(defn- zoom-stage!
  "Zoom the stage about the middle of the view, as its buttons do."
  [scale]
  (when-let [^js area (.querySelector js/document ".stage-area")]
    (let [a (.getBoundingClientRect area)]
      (zoom-at! area (+ (.-left a) (/ (.-clientWidth area) 2))
                (+ (.-top a) (/ (.-clientHeight area) 2)) scale))))

(defn- stepped [k z out?]
  ;; Geometric steps work below 100% as well as above it.
  (if (= :stage k)
    (* z (if out? (/ 1 1.25) 1.25))
    (* z (if out? (/ 1 1.5) 1.5))))

(defn zoomer
  "− <readout> + for one zoomable surface. THE READOUT IS THE BUTTON BACK: it
  says what the zoom is, pressing it returns to normal, and it is accented while
  it has something to return from — so the quick way back is in the one place
  you already look to find out where you are. `more` joins the group: the
  stage's fit."
  [k label & more]
  (let [z @(rf/subscribe [::zoom k])
        [lo hi] (limits k)
        home (k normal)
        to! (if (= :stage k) zoom-stage! #(rf/dispatch [::set k %]))]
    [:div.group.zoomer
     [:button {:disabled (<= z lo) :aria-label (str label " zoom out")
               :title (str "zoom " label " out")
               :on-click #(to! (stepped k z true))} "−"]
     [:button.zoom-at {:class (when (not= z home) "on")
                       :aria-label (str label " zoom")
                       :title (str "back to " (js/Math.round (* 100 home)) "% — the normal zoom")
                       :on-click #(to! home)}
      (str (js/Math.round (* 100 z)) "%")]
     [:button {:disabled (>= z hi) :aria-label (str label " zoom in")
               :title (str "zoom " label " in")
               :on-click #(to! (stepped k z false))} "+"]
     (into [:<>] more)]))


(rf/reg-sub ::mat (fn [db _] (merge {:on? true :opacity 0.55} (get-in db [:ui :passepartout]))))
(rf/reg-event-db ::mat (fn [db [_ k v]] (assoc-in db [:ui :passepartout k] v)))

(rf/reg-sub ::onion
  (fn [db _]
    (merge onion/defaults (get-in db [:ui :onion (:clip/current db) (get-in db [:ui :open])]))))
(rf/reg-event-db ::onion
  (fn [db [_ k v]]
    (assoc-in db [:ui :onion (:clip/current db) (get-in db [:ui :open]) k] v)))

(defn overlay
  "Something drawn over the stage that is not the picture: a switch, and its
  settings in a popover beside it. Tracing, onion and passepartout are all this
  one shape, so they read as three of a kind."
  [class label on? tip toggle! settings]
  [:div.group.overlay {:class class}
   [:button {:class (when on? "on") :aria-pressed (boolean on?) :title tip
             :on-click toggle!}
    label]
   [menu/popover {:label "▾" :title (str label " settings")} settings]])

(defn- slider [label lo hi v on-change]
  [:label label
   [:input {:type "range" :min lo :max hi :step 0.05 :value v
            :on-change #(on-change (js/parseFloat (.. % -target -value)))}]])

(defn onion-controls []
  (let [{:keys [on? before after opacity]} @(rf/subscribe [::onion])]
    [overlay "onion-controls" "onion" on?
     "Onion skin — the frames before and after"
     #(rf/dispatch [::onion :on? (not on?)])
     (fn [_]
       [:div.view-popout
        (for [[k label v] [[:before "Before" before] [:after "After" after]]]
          ^{:key k} [:label label
                     [:input {:type "number" :min 0 :max 5 :value v
                              :on-change #(let [n (js/parseInt (.. % -target -value) 10)]
                                            (when-not (js/isNaN n)
                                              (rf/dispatch [::onion k (max 0 (min 5 n))])))}]])
        [slider "Opacity" 0.05 0.6 opacity #(rf/dispatch [::onion :opacity %])]
        [:small "Before: red · after: blue. Only the selection, when there is one. Hidden during playback."]])]))

(defn centre-stage!
  "Scroll the work area so the stage is in the middle of the view."
  []
  (when-let [^js area (.querySelector js/document ".stage-area")]
    (when-let [wrap (.querySelector area ".stage-wrap")]
      (let [a (.getBoundingClientRect area) s (.getBoundingClientRect wrap)]
        (set! (.-scrollLeft area) (+ (.-scrollLeft area)
                                     (- (+ (.-left s) (/ (.-width s) 2)) (+ (.-left a) (/ (.-clientWidth area) 2)))))
        (set! (.-scrollTop area) (+ (.-scrollTop area)
                                    (- (+ (.-top s) (/ (.-height s) 2)) (+ (.-top a) (/ (.-clientHeight area) 2)))))))))

(defn fit-stage! []
  (when-let [area (.querySelector js/document ".stage-area")]
    (when-let [wrap (.querySelector area ".stage-wrap")]
      (let [w (js/parseFloat (.getAttribute wrap "data-width"))
            h (js/parseFloat (.getAttribute wrap "data-height"))]
        (rf/dispatch-sync [::set :stage (min (/ (- (.-clientWidth area) 32) w)
                                           (/ (- (.-clientHeight area) 32) h))])
        (js/requestAnimationFrame centre-stage!)))))

(defn stage-controls
  "The stage's own view, after the tracing switch: what else is drawn over it,
  then how big it is. Fit is in the zoom group because it IS a zoom."
  []
  (let [{:keys [on? opacity]} @(rf/subscribe [::mat])]
    [:<>
     [onion-controls]
     [overlay "passepartout-controls" "passepartout" on?
      "Darken the desk around the stage"
      #(rf/dispatch [::mat :on? (not on?)])
      (fn [_]
        [:div.view-popout
         [slider "Opacity" 0 1 opacity #(rf/dispatch [::mat :opacity %])]])]
     [:span.sep]
     [zoomer :stage "the stage"
      [:button {:on-click fit-stage! :title "Fit stage in view"} "fit"]]]))
